import { toast } from 'sonner'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BriefcaseBusiness,
  FileText,
  Plus,
  Trash2,
  Upload,
  UserCheck,
  UserX,
  X,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DateInput,
  Input,
  Modal,
  Select,
} from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption } from '@/components/ui'
import { EmptyState } from '@/components/common/EmptyState'
import { PageShell } from '@/components/common/PageShell'
import { useCompanies } from '@/features/companies/hooks/useCompanies'
import { useRoles } from '@/features/roles/hooks/useRoles'
import { openDocumentInNewTab } from '@/api/services/documents.service'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { userSubline } from '@/lib/formatters'
import { usePermissions } from '@/hooks/usePermissions'
import {
  DOCUMENT_UPLOAD_ACCEPT,
  formatFileSize,
  getDocumentUploadHelpText,
  validateDocumentFiles,
} from '@/features/documents/utils/documentUtils'
import {
  useCandidateDocuments,
  useCandidates,
  useCreateCandidate,
  useDeleteCandidateDocument,
  useHireCandidate,
  useRejectCandidate,
  useUpdateCandidate,
  useUploadCandidateDocuments,
} from '../hooks/useCandidates'
import type {
  Candidate,
  CandidatePayload,
  CandidateStatus,
  HireCandidatePayload,
  ProbationPeriod,
} from '@/api/services/candidates.service'

const STATUS_OPTIONS: SelectOption[] = [
  { value: '', label: 'All statuses' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'trainee', label: 'Trainee' },
  { value: 'offered', label: 'Offered' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
]

const CANDIDATE_DOCUMENT_OPTIONS: SelectOption[] = [
  { value: 'interview-form', label: 'Interview Form' },
  { value: 'candidate-documents', label: 'Candidate Documents' },
]

const CANDIDATE_DOCUMENT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Interview Form', label: 'Interview Form' },
  { value: 'CNIC', label: 'CNIC' },
  { value: 'Passport', label: 'Passport' },
  { value: 'Resume / CV', label: 'Resume / CV' },
  { value: 'Certificate', label: 'Certificate' },
  { value: 'Reference', label: 'Reference' },
  { value: 'Other', label: 'Other' },
]

const WORK_LOCATION_OPTIONS: SelectOption[] = [
  { value: 'onsite', label: 'Onsite' },
  { value: 'remote', label: 'Remote' },
  { value: 'hybrid', label: 'Hybrid' },
]

const COMPENSATION_OPTIONS: SelectOption[] = [
  { value: 'salaried', label: 'Salaried' },
  { value: 'hourly', label: 'Hourly' },
]

const PROBATION_OPTIONS: SelectOption[] = [
  { value: '3-months', label: '90 days' },
  { value: '1-month', label: '30 days' },
  { value: 'custom', label: 'Custom date' },
]

const HIRE_OUTCOME_OPTIONS: SelectOption[] = [
  { value: 'probation', label: 'Hire on probation' },
  { value: 'permanent', label: 'Hire as full-time' },
]

function statusVariant(
  status: CandidateStatus,
): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  if (status === 'hired') return 'success'
  if (status === 'rejected') return 'danger'
  if (status === 'offered') return 'warning'
  if (status === 'trainee') return 'neutral'
  return 'info'
}

function fullName(candidate: Pick<Candidate, 'firstName' | 'lastName' | 'email'>): string {
  return `${candidate.firstName} ${candidate.lastName}`.trim() || candidate.email
}

function emptyCandidate(): CandidatePayload {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    fatherName: '',
    motherName: '',
    cnicNumber: '',
    dateOfBirth: '',
    qualification: '',
    maritalStatus: '',
    address: '',
    emergencyContactNumber: '',
    bankName: '',
    accountTitle: '',
    branch: '',
    branchCode: '',
    accountNumber: '',
    appliedPosition: '',
    source: '',
    interviewDate: '',
    notes: '',
    status: 'interviewing',
  }
}

function toDateInput(value?: string): string {
  return value ? value.slice(0, 10) : ''
}

const PHONE_PATTERN = /^\+?[0-9][0-9\s()-]{6,19}$/
const CNIC_PATTERN = /^\d{5}-?\d{7}-?\d$/
const ACCOUNT_NUMBER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9 -]{5,33}$/
const BRANCH_CODE_PATTERN = /^\d{3,6}$/
const NAME_PATTERN = /^[A-Za-z][A-Za-z .'-]{1,79}$/

type CandidateFormErrors = Partial<Record<keyof CandidatePayload, string>>
type HireFormErrors = Partial<Record<keyof HireCandidatePayload, string>>

function isBlank(value: unknown): boolean {
  return typeof value !== 'string' || value.trim() === ''
}

function formatPakistaniCnic(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 13)
  if (digits.length <= 5) return digits
  if (digits.length <= 12) return `${digits.slice(0, 5)}-${digits.slice(5)}`
  return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`
}

function validateCandidateValues(values: CandidatePayload): CandidateFormErrors {
  const errors: CandidateFormErrors = {}
  if (isBlank(values.firstName)) errors.firstName = 'First name is required'
  else if (!NAME_PATTERN.test(values.firstName.trim())) {
    errors.firstName = 'Use letters, spaces, hyphens, apostrophes, or periods'
  }
  if (isBlank(values.lastName)) errors.lastName = 'Last name is required'
  else if (!NAME_PATTERN.test(values.lastName.trim())) {
    errors.lastName = 'Use letters, spaces, hyphens, apostrophes, or periods'
  }
  if (values.fatherName?.trim() && !NAME_PATTERN.test(values.fatherName.trim())) {
    errors.fatherName = 'Use letters, spaces, hyphens, apostrophes, or periods'
  }
  if (values.motherName?.trim() && !NAME_PATTERN.test(values.motherName.trim())) {
    errors.motherName = 'Use letters, spaces, hyphens, apostrophes, or periods'
  }
  if (isBlank(values.email)) errors.email = 'Email is required'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Enter a valid email address'
  }
  if (values.phone?.trim() && !PHONE_PATTERN.test(values.phone.trim())) {
    errors.phone = 'Enter a valid contact number'
  }
  if (
    values.emergencyContactNumber?.trim() &&
    !PHONE_PATTERN.test(values.emergencyContactNumber.trim())
  ) {
    errors.emergencyContactNumber = 'Enter a valid emergency contact number'
  }
  if (values.cnicNumber?.trim() && !CNIC_PATTERN.test(values.cnicNumber.trim())) {
    errors.cnicNumber = 'CNIC must be 13 digits, with optional dashes'
  }
  if (
    values.expectedSalary !== undefined &&
    (!Number.isFinite(values.expectedSalary) || values.expectedSalary < 0)
  ) {
    errors.expectedSalary = 'Expected salary must be zero or more'
  }
  if (values.bankName?.trim() && values.bankName.trim().length < 2) {
    errors.bankName = 'Bank name must be at least 2 characters'
  }
  if (values.accountTitle?.trim() && !NAME_PATTERN.test(values.accountTitle.trim())) {
    errors.accountTitle = 'Use the account holder name'
  }
  if (values.branchCode?.trim() && !BRANCH_CODE_PATTERN.test(values.branchCode.trim())) {
    errors.branchCode = 'Branch code must be 3 to 6 digits'
  }
  if (values.accountNumber?.trim() && !ACCOUNT_NUMBER_PATTERN.test(values.accountNumber.trim())) {
    errors.accountNumber = 'Account number must be 6 to 34 letters or digits'
  }
  return errors
}

function validateHireValues(payload: HireCandidatePayload): HireFormErrors {
  const errors: HireFormErrors = {}
  if (isBlank(payload.roleId)) errors.roleId = 'Role is required'
  if (isBlank(payload.companyId)) errors.companyId = 'Company is required'
  if (isBlank(payload.joiningDate)) errors.joiningDate = 'Joining date is required'
  if (payload.compensationType === 'hourly') {
    if (!Number.isFinite(payload.hourlyRate) || (payload.hourlyRate ?? -1) < 0) {
      errors.hourlyRate = 'Hourly rate must be zero or more'
    }
  } else if (!Number.isFinite(payload.baseSalary) || (payload.baseSalary ?? -1) < 0) {
    errors.baseSalary = 'Base salary must be zero or more'
  }
  if (
    payload.employmentStatus !== 'permanent' &&
    payload.probationPeriod === 'custom' &&
    isBlank(payload.probationEndDate)
  ) {
    errors.probationEndDate = 'Probation end date is required'
  }
  if (payload.bankName?.trim() && payload.bankName.trim().length < 2) {
    errors.bankName = 'Bank name must be at least 2 characters'
  }
  if (payload.accountTitle?.trim() && !NAME_PATTERN.test(payload.accountTitle.trim())) {
    errors.accountTitle = 'Use the account holder name'
  }
  if (payload.branchCode?.trim() && !BRANCH_CODE_PATTERN.test(payload.branchCode.trim())) {
    errors.branchCode = 'Branch code must be 3 to 6 digits'
  }
  if (payload.accountNumber?.trim() && !ACCOUNT_NUMBER_PATTERN.test(payload.accountNumber.trim())) {
    errors.accountNumber = 'Account number must be 6 to 34 letters or digits'
  }
  return errors
}

function compactPayload(values: CandidatePayload): CandidatePayload {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    email: values.email.trim(),
    ...(values.phone?.trim() ? { phone: values.phone.trim() } : {}),
    ...(values.fatherName?.trim() ? { fatherName: values.fatherName.trim() } : {}),
    ...(values.motherName?.trim() ? { motherName: values.motherName.trim() } : {}),
    ...(values.cnicNumber?.trim() ? { cnicNumber: values.cnicNumber.trim() } : {}),
    ...(values.dateOfBirth ? { dateOfBirth: values.dateOfBirth } : {}),
    ...(values.qualification?.trim() ? { qualification: values.qualification.trim() } : {}),
    ...(values.maritalStatus?.trim() ? { maritalStatus: values.maritalStatus.trim() } : {}),
    ...(values.address?.trim() ? { address: values.address.trim() } : {}),
    ...(values.emergencyContactNumber?.trim()
      ? { emergencyContactNumber: values.emergencyContactNumber.trim() }
      : {}),
    ...(values.bankName?.trim() ? { bankName: values.bankName.trim() } : {}),
    ...(values.accountTitle?.trim() ? { accountTitle: values.accountTitle.trim() } : {}),
    ...(values.branch?.trim() ? { branch: values.branch.trim() } : {}),
    ...(values.branchCode?.trim() ? { branchCode: values.branchCode.trim() } : {}),
    ...(values.accountNumber?.trim() ? { accountNumber: values.accountNumber.trim() } : {}),
    ...(values.companyId ? { companyId: values.companyId } : {}),
    ...(values.appliedPosition?.trim() ? { appliedPosition: values.appliedPosition.trim() } : {}),
    ...(values.source?.trim() ? { source: values.source.trim() } : {}),
    ...(Number.isFinite(values.expectedSalary) ? { expectedSalary: values.expectedSalary } : {}),
    ...(values.interviewDate ? { interviewDate: values.interviewDate } : {}),
    ...(values.notes?.trim() ? { notes: values.notes.trim() } : {}),
    ...(values.status ? { status: values.status } : {}),
  }
}

interface CandidateFormProps {
  candidate: Candidate | null
  companyOptions: SelectOption[]
  onSaved: (candidate: Candidate) => void
}

function CandidateForm({
  candidate,
  companyOptions,
  onSaved,
}: CandidateFormProps): React.ReactElement {
  const createCandidate = useCreateCandidate()
  const updateCandidate = useUpdateCandidate()
  const [values, setValues] = useState<CandidatePayload>(emptyCandidate)
  const [errors, setErrors] = useState<CandidateFormErrors>({})

  useEffect(() => {
    if (!candidate) {
      setValues(emptyCandidate())
      return
    }
    setValues({
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      email: candidate.email,
      phone: candidate.phone ?? '',
      fatherName: candidate.fatherName ?? '',
      motherName: candidate.motherName ?? '',
      cnicNumber: candidate.cnicNumber ?? '',
      dateOfBirth: toDateInput(candidate.dateOfBirth),
      qualification: candidate.qualification ?? '',
      maritalStatus: candidate.maritalStatus ?? '',
      address: candidate.address ?? '',
      emergencyContactNumber: candidate.emergencyContactNumber ?? '',
      bankName: candidate.bankName ?? '',
      accountTitle: candidate.accountTitle ?? '',
      branch: candidate.branch ?? '',
      branchCode: candidate.branchCode ?? '',
      accountNumber: candidate.accountNumber ?? '',
      companyId: candidate.companyId,
      appliedPosition: candidate.appliedPosition ?? '',
      source: candidate.source ?? '',
      expectedSalary: candidate.expectedSalary,
      interviewDate: toDateInput(candidate.interviewDate),
      notes: candidate.notes ?? '',
      status: candidate.status,
    })
  }, [candidate])

  const set = (key: keyof CandidatePayload, value: string | number | undefined): void => {
    setValues((current) => ({ ...current, [key]: value }))
    setErrors((current) => ({ ...current, [key]: undefined }))
  }

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    const nextErrors = validateCandidateValues(values)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    const payload = compactPayload(values)
    if (candidate) {
      updateCandidate.mutate({ id: candidate._id, payload }, { onSuccess: (next) => onSaved(next) })
      return
    }
    createCandidate.mutate(payload, { onSuccess: (next) => onSaved(next) })
  }

  const pending = createCandidate.isPending || updateCandidate.isPending

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <Input
          label="First name"
          required
          value={values.firstName}
          error={errors.firstName}
          onChange={(event) => set('firstName', event.target.value)}
        />
        <Input
          label="Last name"
          required
          value={values.lastName}
          error={errors.lastName}
          onChange={(event) => set('lastName', event.target.value)}
        />
        <Input
          label="Email"
          type="email"
          required
          value={values.email}
          error={errors.email}
          onChange={(event) => set('email', event.target.value)}
        />
        <Input
          label="Phone"
          value={values.phone ?? ''}
          error={errors.phone}
          onChange={(event) => set('phone', event.target.value)}
        />
        <Input
          label="Father's name"
          value={values.fatherName ?? ''}
          error={errors.fatherName}
          onChange={(event) => set('fatherName', event.target.value)}
        />
        <Input
          label="Mother's name"
          value={values.motherName ?? ''}
          error={errors.motherName}
          onChange={(event) => set('motherName', event.target.value)}
        />
        <Input
          label="CNIC number"
          value={values.cnicNumber ?? ''}
          error={errors.cnicNumber}
          placeholder="12345-1234567-1"
          inputMode="numeric"
          maxLength={15}
          onChange={(event) => set('cnicNumber', formatPakistaniCnic(event.target.value))}
        />
        <DateInput
          label="Date of birth"
          value={values.dateOfBirth ?? ''}
          onChange={(event) => set('dateOfBirth', event.target.value)}
        />
        <Input
          label="Qualification"
          value={values.qualification ?? ''}
          onChange={(event) => set('qualification', event.target.value)}
        />
        <Input
          label="Marital status"
          value={values.maritalStatus ?? ''}
          onChange={(event) => set('maritalStatus', event.target.value)}
        />
        <Input
          label="Emergency contact number"
          value={values.emergencyContactNumber ?? ''}
          error={errors.emergencyContactNumber}
          onChange={(event) => set('emergencyContactNumber', event.target.value)}
        />
        <Input
          label="Applied position"
          value={values.appliedPosition ?? ''}
          onChange={(event) => set('appliedPosition', event.target.value)}
        />
        <Input
          label="Reference"
          value={values.source ?? ''}
          onChange={(event) => set('source', event.target.value)}
        />
        <Input
          label="Expected salary"
          type="number"
          step="0.01"
          min="0"
          value={values.expectedSalary ?? ''}
          error={errors.expectedSalary}
          onChange={(event) =>
            set('expectedSalary', event.target.value ? Number(event.target.value) : undefined)
          }
        />
        <DateInput
          label="Interview date"
          value={values.interviewDate ?? ''}
          onChange={(event) => set('interviewDate', event.target.value)}
        />
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Company
          </label>
          <Select
            options={[{ value: '', label: 'No company selected' }, ...companyOptions]}
            value={values.companyId ?? ''}
            onChange={(value) => set('companyId', value || undefined)}
            placeholder="Select company"
            searchable
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Status
          </label>
          <Select
            options={STATUS_OPTIONS.filter((option) => option.value)}
            value={values.status ?? 'interviewing'}
            onChange={(value) => set('status', value as CandidateStatus)}
          />
        </div>
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Account details</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <Input
            label="Bank name"
            value={values.bankName ?? ''}
            error={errors.bankName}
            onChange={(event) => set('bankName', event.target.value)}
          />
          <Input
            label="Account title"
            value={values.accountTitle ?? ''}
            error={errors.accountTitle}
            onChange={(event) => set('accountTitle', event.target.value)}
          />
          <Input
            label="Branch"
            value={values.branch ?? ''}
            onChange={(event) => set('branch', event.target.value)}
          />
          <Input
            label="Branch code"
            value={values.branchCode ?? ''}
            error={errors.branchCode}
            onChange={(event) => set('branchCode', event.target.value)}
          />
          <Input
            label="Account number"
            value={values.accountNumber ?? ''}
            error={errors.accountNumber}
            onChange={(event) => set('accountNumber', event.target.value)}
          />
        </div>
      </div>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Address
        </span>
        <textarea
          value={values.address ?? ''}
          onChange={(event) => set('address', event.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Notes
        </span>
        <textarea
          value={values.notes ?? ''}
          onChange={(event) => set('notes', event.target.value)}
          rows={4}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
      </label>
      <div className="flex justify-end">
        <Button type="submit" loading={pending} className="gap-2">
          <Plus className="h-4 w-4" />
          {candidate ? 'Save candidate' : 'Create candidate'}
        </Button>
      </div>
    </form>
  )
}

interface CandidateDocumentsProps {
  candidate: Candidate | null
}

function CandidateDocuments({ candidate }: CandidateDocumentsProps): React.ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [category, setCategory] = useState<'interview-form' | 'candidate-documents'>(
    'interview-form',
  )
  const [documentType, setDocumentType] = useState('Interview Form')
  const [customDocumentType, setCustomDocumentType] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [documentToDelete, setDocumentToDelete] = useState<{ id: string; name: string } | null>(
    null,
  )
  const docsQuery = useCandidateDocuments(candidate?._id ?? null)
  const upload = useUploadCandidateDocuments(candidate?._id ?? null)
  const deleteDocument = useDeleteCandidateDocument(candidate?._id ?? null)
  const { can } = usePermissions()
  const canDeleteDocuments = can('documents', 'manage_staff_others')

  const processFiles = (files: FileList | File[]): void => {
    const list = Array.from(files)
    const error = validateDocumentFiles(list)
    if (error) {
      setUploadError(error)
      setSelectedFiles([])
      return
    }
    setUploadError(null)
    setSelectedFiles((current) => [...current, ...list])
  }

  const handleUpload = (): void => {
    if (!candidate || selectedFiles.length === 0) return
    const resolvedDocumentType =
      category === 'interview-form'
        ? 'Interview Form'
        : documentType === 'Other'
          ? customDocumentType.trim()
          : documentType
    if (category === 'candidate-documents' && !resolvedDocumentType) {
      setUploadError('Enter the document type before uploading')
      return
    }
    upload.mutate(
      { files: selectedFiles, category, documentType: resolvedDocumentType },
      {
        onSuccess: () => {
          setSelectedFiles([])
          setUploadError(null)
        },
        onError: (error) => setUploadError(getErrorMessage(error)),
      },
    )
  }

  const handleConfirmDelete = (): void => {
    if (!documentToDelete) return
    deleteDocument.mutate(documentToDelete.id, {
      onSuccess: () => setDocumentToDelete(null),
    })
  }

  if (!candidate) {
    return (
      <EmptyState
        icon={FileText}
        title="No candidate selected"
        description="Select or create a candidate to manage interview documents."
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-[14rem_minmax(0,1fr)_10rem] md:items-stretch">
        <Select
          options={CANDIDATE_DOCUMENT_OPTIONS}
          value={category}
          onChange={(value) => {
            const nextCategory = value as 'interview-form' | 'candidate-documents'
            setCategory(nextCategory)
            setDocumentType(nextCategory === 'interview-form' ? 'Interview Form' : 'CNIC')
            setCustomDocumentType('')
            setUploadError(null)
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-11 min-h-11 items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-3 text-sm font-medium text-gray-700 transition-colors hover:border-primary/60 hover:bg-primary/5 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
        >
          <Upload className="h-4 w-4 text-primary" />
          {selectedFiles.length > 0
            ? `${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'} selected`
            : 'Drop or browse files'}
        </button>
        <Button
          type="button"
          size="lg"
          disabled={selectedFiles.length === 0}
          loading={upload.isPending}
          onClick={handleUpload}
          className="h-11 min-h-11 w-full gap-2 rounded-xl px-4 text-sm"
        >
          <Upload className="h-4 w-4" />
          Upload
        </Button>
      </div>
      {category === 'candidate-documents' && (
        <div className="grid gap-3 md:grid-cols-[14rem_minmax(0,1fr)]">
          <Select
            label="Document type"
            options={CANDIDATE_DOCUMENT_TYPE_OPTIONS.filter(
              (option) => option.value !== 'Interview Form',
            )}
            value={documentType === 'Interview Form' ? 'CNIC' : documentType}
            onChange={(value) => {
              setDocumentType(value)
              setUploadError(null)
            }}
          />
          {documentType === 'Other' && (
            <Input
              label="Custom document type"
              value={customDocumentType}
              onChange={(event) => {
                setCustomDocumentType(event.target.value)
                setUploadError(null)
              }}
              placeholder="e.g. Driving license, transcript, portfolio"
            />
          )}
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_UPLOAD_ACCEPT}
        multiple
        className="sr-only"
        onChange={(event) => {
          if (event.target.files) processFiles(event.target.files)
          event.target.value = ''
        }}
      />
      <p className="text-xs text-gray-500 dark:text-gray-400">{getDocumentUploadHelpText()}</p>
      {uploadError && (
        <p className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
          {uploadError}
        </p>
      )}
      {selectedFiles.length > 0 && (
        <ul className="space-y-1">
          {selectedFiles.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1.5 text-sm dark:border-gray-700 dark:bg-gray-900"
            >
              <FileText className="h-4 w-4 text-gray-500" />
              <span className="min-w-0 flex-1 truncate">{file.name}</span>
              <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
              <Button
                type="button"
                variant="ghost"
                className="h-7 min-h-7 px-1"
                onClick={() => setSelectedFiles((current) => current.filter((_, i) => i !== index))}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {docsQuery.isLoading ? (
        <SectionLoader variant="list" rows={3} label="Loading candidate documents" />
      ) : (docsQuery.data ?? []).length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No candidate documents yet"
          description="Upload interview forms or supporting candidate files."
        />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
          {(docsQuery.data ?? []).map((doc) => (
            <li key={doc.id} className="flex items-center gap-3 px-3 py-2">
              <FileText className="h-4 w-4 text-gray-500" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                  {doc.originalName}
                </p>
                <p className="text-xs text-gray-500">
                  {(doc.documentType || doc.category).replaceAll('-', ' ')} ·{' '}
                  {formatFileSize(doc.size)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  void openDocumentInNewTab(doc).catch(() => {
                    toast.error(`Could not open ${doc.originalName}`)
                  })
                }}
                className="text-sm font-medium text-primary hover:underline"
              >
                Open
              </button>
              {canDeleteDocuments && (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-8 min-h-8 px-2 text-danger hover:bg-danger/10"
                  loading={deleteDocument.isPending}
                  onClick={() => setDocumentToDelete({ id: doc.id, name: doc.originalName })}
                  aria-label={`Remove ${doc.originalName}`}
                  loadingLabel={`Removing ${doc.originalName}`}
                >
                  {deleteDocument.isPending ? 'Removing…' : <Trash2 className="h-4 w-4" />}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={!!documentToDelete}
        onClose={() => setDocumentToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Remove document"
        message={
          documentToDelete
            ? `Remove ${documentToDelete.name} from this candidate profile?`
            : 'Remove this document from this candidate profile?'
        }
        confirmLabel="Remove"
        variant="danger"
        confirmLoading={deleteDocument.isPending}
      />
    </div>
  )
}

interface HireModalProps {
  candidate: Candidate | null
  companyOptions: SelectOption[]
  roleOptions: SelectOption[]
  onClose: () => void
}

function HireModal({
  candidate,
  companyOptions,
  roleOptions,
  onClose,
}: HireModalProps): React.ReactElement {
  const hire = useHireCandidate()
  const { currency } = useOrgCurrency()
  const [errors, setErrors] = useState<HireFormErrors>({})
  const [payload, setPayload] = useState<HireCandidatePayload>({
    roleId: '',
    companyId: '',
    joiningDate: new Date().toISOString().slice(0, 10),
    workLocation: 'onsite',
    compensationType: 'salaried',
    baseSalary: 0,
    employmentStatus: 'probation',
    probationPeriod: '3-months',
  })

  useEffect(() => {
    if (!candidate) return
    setPayload((current) => ({
      ...current,
      companyId: candidate.companyId ?? '',
      designation: current.designation ?? candidate.appliedPosition,
      bankName: candidate.bankName ?? '',
      accountTitle: candidate.accountTitle ?? '',
      branch: candidate.branch ?? '',
      branchCode: candidate.branchCode ?? '',
      accountNumber: candidate.accountNumber ?? '',
    }))
  }, [candidate])

  const set = (key: keyof HireCandidatePayload, value: string | number | undefined): void => {
    setPayload((current) => ({ ...current, [key]: value }))
    setErrors((current) => ({ ...current, [key]: undefined }))
  }

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault()
    if (!candidate) return
    const nextErrors = validateHireValues(payload)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    hire.mutate(
      {
        id: candidate._id,
        payload: {
          ...payload,
          currency,
          designation: payload.designation || candidate.appliedPosition,
        },
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      open={!!candidate}
      onClose={onClose}
      title="Hire candidate"
      className="max-w-2xl"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button form="hire-candidate-form" type="submit" loading={hire.isPending}>
            {hire.isPending ? 'Hiring…' : 'Hire candidate'}
          </Button>
        </>
      }
    >
      <form id="hire-candidate-form" onSubmit={handleSubmit} className="space-y-4">
        {candidate && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Create user account for{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {fullName(candidate)}
            </span>
            .
          </p>
        )}
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <Select
              label="Role"
              options={roleOptions}
              value={payload.roleId}
              onChange={(value) => set('roleId', value)}
              placeholder="Select role"
              searchable
            />
            {errors.roleId && <p className="mt-1 text-sm text-danger">{errors.roleId}</p>}
          </div>
          <div>
            <Select
              label="Company"
              options={companyOptions}
              value={payload.companyId}
              onChange={(value) => set('companyId', value)}
              placeholder="Select company"
              searchable
            />
            {errors.companyId && <p className="mt-1 text-sm text-danger">{errors.companyId}</p>}
          </div>
          <DateInput
            label="Joining date"
            required
            value={payload.joiningDate}
            error={errors.joiningDate}
            onChange={(event) => set('joiningDate', event.target.value)}
          />
          <Select
            label="Work location"
            options={WORK_LOCATION_OPTIONS}
            value={payload.workLocation ?? 'onsite'}
            onChange={(value) => set('workLocation', value)}
          />
          <Input
            label="Department"
            value={payload.department ?? ''}
            onChange={(event) => set('department', event.target.value)}
          />
          <Input
            label="Designation"
            value={payload.designation ?? candidate?.appliedPosition ?? ''}
            onChange={(event) => set('designation', event.target.value)}
          />
          <Select
            label="Compensation type"
            options={COMPENSATION_OPTIONS}
            value={payload.compensationType}
            onChange={(value) => {
              set('compensationType', value)
              if (value === 'hourly') set('baseSalary', undefined)
              else set('hourlyRate', undefined)
            }}
          />
          {payload.compensationType === 'hourly' ? (
            <Input
              label={`Hourly rate (${currency})`}
              type="number"
              step="0.01"
              min="0"
              required
              value={payload.hourlyRate ?? ''}
              error={errors.hourlyRate}
              onChange={(event) => set('hourlyRate', Number(event.target.value))}
            />
          ) : (
            <Input
              label={`Base salary (${currency})`}
              type="number"
              step="0.01"
              min="0"
              required
              value={payload.baseSalary ?? ''}
              error={errors.baseSalary}
              onChange={(event) => set('baseSalary', Number(event.target.value))}
            />
          )}
          <Select
            label="Employment outcome"
            options={HIRE_OUTCOME_OPTIONS}
            value={payload.employmentStatus ?? 'probation'}
            onChange={(value) => {
              set('employmentStatus', value)
              if (value === 'permanent') set('probationEndDate', undefined)
            }}
          />
          {payload.employmentStatus !== 'permanent' && (
            <Select
              label="Probation period"
              options={PROBATION_OPTIONS}
              value={payload.probationPeriod ?? '3-months'}
              onChange={(value) => set('probationPeriod', value as ProbationPeriod)}
            />
          )}
          {payload.employmentStatus !== 'permanent' && payload.probationPeriod === 'custom' && (
            <DateInput
              label="Probation end date"
              required
              value={payload.probationEndDate ?? ''}
              error={errors.probationEndDate}
              onChange={(event) => set('probationEndDate', event.target.value)}
            />
          )}
          <div className="md:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Account details
            </h3>
          </div>
          <Input
            label="Bank name"
            value={payload.bankName ?? ''}
            error={errors.bankName}
            onChange={(event) => set('bankName', event.target.value)}
          />
          <Input
            label="Account title"
            value={payload.accountTitle ?? ''}
            error={errors.accountTitle}
            onChange={(event) => set('accountTitle', event.target.value)}
          />
          <Input
            label="Branch"
            value={payload.branch ?? ''}
            onChange={(event) => set('branch', event.target.value)}
          />
          <Input
            label="Branch code"
            value={payload.branchCode ?? ''}
            error={errors.branchCode}
            onChange={(event) => set('branchCode', event.target.value)}
          />
          <Input
            label="Account number"
            value={payload.accountNumber ?? ''}
            error={errors.accountNumber}
            onChange={(event) => set('accountNumber', event.target.value)}
          />
        </div>
      </form>
    </Modal>
  )
}

export function CandidatesPage(): React.ReactElement {
  const [status, setStatus] = useState('')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [hireCandidate, setHireCandidate] = useState<Candidate | null>(null)
  const [rejectCandidate, setRejectCandidate] = useState<Candidate | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const { data: companies = [] } = useCompanies()
  const { data: roles = [] } = useRoles()
  const rejectMutation = useRejectCandidate()
  const params = {
    ...(status ? { status: status as CandidateStatus } : {}),
    ...(search.trim() ? { search: search.trim() } : {}),
  }
  const candidatesQuery = useCandidates(params)
  const candidates = candidatesQuery.data ?? []

  useEffect(() => {
    if (!isCreatingNew && !selectedId && candidates[0]?._id) setSelectedId(candidates[0]._id)
  }, [candidates, isCreatingNew, selectedId])

  const selectedCandidate = useMemo(
    () =>
      isCreatingNew ? null : (candidates.find((candidate) => candidate._id === selectedId) ?? null),
    [candidates, isCreatingNew, selectedId],
  )
  const companyOptions: SelectOption[] = companies.map((company) => ({
    value: company._id,
    label: company.name,
  }))
  const roleOptions: SelectOption[] = roles.map((role) => ({
    value: role._id,
    label: role.name,
  }))

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Workforce', to: '/dashboard' }, { label: 'Candidates' }]}
      icon={BriefcaseBusiness}
      title="Candidates"
      description="Manage interview records, candidate documents, probation hires, and hiring decisions."
    >
      <div className="grid gap-4 lg:grid-cols-[24rem_minmax(0,1fr)]">
        <Card contentClassName="space-y-4 p-4">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Candidate pipeline
            </h2>
            <Button
              size="sm"
              onClick={() => {
                setSelectedId(null)
                setIsCreatingNew(true)
              }}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              New
            </Button>
          </div>
          <div className="grid gap-2">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search candidates"
            />
            <Select
              options={STATUS_OPTIONS}
              value={status}
              onChange={setStatus}
              placeholder="All statuses"
            />
          </div>
          {candidatesQuery.isLoading ? (
            <SectionLoader variant="list" label="Loading candidates" />
          ) : candidates.length === 0 ? (
            <EmptyState
              icon={BriefcaseBusiness}
              title="No candidates yet"
              description="Create the first interview record."
            />
          ) : (
            <div className="space-y-2">
              {candidates.map((candidate) => (
                <button
                  key={candidate._id}
                  type="button"
                  onClick={() => {
                    setSelectedId(candidate._id)
                    setIsCreatingNew(false)
                  }}
                  className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                    selectedCandidate?._id === candidate._id
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-200 bg-white hover:border-primary/50 dark:border-gray-700 dark:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {fullName(candidate)}
                      </p>
                      <p className="truncate text-xs text-gray-500">
                        {userSubline(candidate.appliedPosition, candidate.email)}
                      </p>
                    </div>
                    <Badge variant={statusVariant(candidate.status)}>
                      {candidate.status.replace('-', ' ')}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card contentClassName="space-y-4 p-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {selectedCandidate ? fullName(selectedCandidate) : 'New candidate'}
                </h2>
                {selectedCandidate && (
                  <p className="text-sm text-gray-500">{selectedCandidate.email}</p>
                )}
              </div>
              {selectedCandidate && selectedCandidate.status !== 'hired' && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    className="gap-2"
                    onClick={() => setHireCandidate(selectedCandidate)}
                  >
                    <UserCheck className="h-4 w-4" />
                    Hire
                  </Button>
                  {selectedCandidate.status !== 'rejected' && (
                    <Button
                      variant="danger"
                      className="gap-2"
                      onClick={() => setRejectCandidate(selectedCandidate)}
                    >
                      <UserX className="h-4 w-4" />
                      Reject
                    </Button>
                  )}
                </div>
              )}
            </div>
            <CandidateForm
              candidate={selectedCandidate}
              companyOptions={companyOptions}
              onSaved={(candidate) => {
                setSelectedId(candidate._id)
                setIsCreatingNew(false)
              }}
            />
          </Card>

          <Card contentClassName="space-y-4 p-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Interview documents
            </h2>
            <CandidateDocuments candidate={selectedCandidate} />
          </Card>
        </div>
      </div>

      <HireModal
        candidate={hireCandidate}
        companyOptions={companyOptions}
        roleOptions={roleOptions}
        onClose={() => setHireCandidate(null)}
      />

      <Modal
        open={!!rejectCandidate}
        onClose={() => setRejectCandidate(null)}
        title="Reject candidate"
        className="max-w-md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRejectCandidate(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={rejectMutation.isPending}
              onClick={() => {
                if (!rejectCandidate) return
                rejectMutation.mutate(
                  { id: rejectCandidate._id, reason: rejectReason || undefined },
                  {
                    onSuccess: () => {
                      setRejectCandidate(null)
                      setRejectReason('')
                    },
                  },
                )
              }}
            >
              {rejectMutation.isPending ? 'Rejecting…' : 'Reject candidate'}
            </Button>
          </>
        }
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason
          </span>
          <textarea
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
            rows={4}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
        </label>
      </Modal>
    </PageShell>
  )
}

CandidatesPage.displayName = 'CandidatesPage'
